import { FC } from 'react';
import { SidePanel } from '@carbon/ibm-products';
import styles from './structuring-module.module.scss';

// Ported from the mock `WellScoringGlossaryPanel`. Reconciled to the SIX
// weighted factors agreed with Product (the tech spec's CONFIDENCE_FACTORS) —
// the mock listed seven; the backend persists six. Threshold copy matches the
// configurable 40% persist floor.

interface Props {
  open: boolean;
  onRequestClose: () => void;
  /**
   * Shell root the slide-in SidePanel anchors its margin to. Defaults to the AFE
   * structuring shell; the DO structuring shell passes `.do-structuring-shell`.
   */
  selectorPageContent?: string;
}

const FACTORS: { name: string; weight: string; desc: string }[] = [
  {
    name: 'API Number / UWI',
    weight: '35% of score',
    desc: 'How closely the candidate’s UWI or API number matches the extracted identifier — including exact matches, partial matches, and formatted variations (with or without dashes).',
  },
  {
    name: 'Well Name',
    weight: '25% of score',
    desc: 'How closely the candidate’s well name matches the extracted name — including exact matches, abbreviations, name-order variations, and known aliases.',
  },
  {
    name: 'Legal Description',
    weight: '15% of score',
    desc: 'Whether the candidate’s Section-Township-Range / subdivision legal description matches the extracted well’s. Exact-match legal descriptions score highest.',
  },
  {
    name: 'SHL & BHL',
    weight: '10% of score',
    desc: 'Whether the candidate’s surface hole location (SHL) and bottom hole location (BHL) coordinates are consistent with the extracted well’s location data.',
  },
  {
    name: 'Formation & Basin',
    weight: '10% of score',
    desc: 'Whether the candidate’s formation and basin match the extracted well’s. Exact formation match scores highest; same basin only scores moderately.',
  },
  {
    name: 'Operator Name',
    weight: '5% of score',
    desc: 'Whether the operator associated with the candidate well matches the operator or sender identified in this document.',
  },
];

export const WellScoringGlossaryPanel: FC<Props> = ({
  open,
  onRequestClose,
  selectorPageContent = '.afe-structuring-shell',
}) => (
  <SidePanel
    open={open}
    onRequestClose={onRequestClose}
    title="Scoring Glossary"
    subtitle="How the agent evaluates each well match suggestion."
    slideIn
    selectorPageContent={selectorPageContent}
    placement="right"
    size="md"
  >
    <div className={styles.glossary}>
      <div className={styles.glossarySection}>
        <p className={styles.glossaryBody}>
          The Scoring Glossary explains how the Well Matching Agent evaluates
          each well match suggestion and builds the confidence score.
        </p>
      </div>

      <hr className={styles.glossaryDivider} />

      <div className={styles.glossarySection}>
        <h4 className={styles.glossaryHeading}>Confidence</h4>
        <p className={styles.glossaryBody}>
          The agent&rsquo;s recorded confidence for this match, on a
          0&ndash;100% scale. Scores above ~40% are persisted as suggestions;
          below that threshold the agent returns &ldquo;no match&rdquo;.
        </p>
      </div>

      <hr className={styles.glossaryDivider} />

      <div className={styles.glossarySection}>
        <h4 className={styles.glossaryHeading}>
          How the confidence score is built
        </h4>
        <p className={styles.glossaryBody}>
          The agent evaluates this match against six factors, each weighted by
          how much it contributes to the final score. The Weight column shows
          each factor&rsquo;s share; the Score column shows this
          candidate&rsquo;s weighted contribution. All weights sum to 100%.
        </p>
        <code className={styles.glossaryFormula}>
          0.35&middot;uwi + 0.25&middot;name + 0.15&middot;legal +
          0.10&middot;shl_bhl + 0.10&middot;formation + 0.05&middot;operator
        </code>
      </div>

      <hr className={styles.glossaryDivider} />

      <div className={styles.glossarySection}>
        <h4 className={styles.glossaryHeading}>The Six Factors</h4>
        <dl className={styles.glossaryFactors}>
          {FACTORS.map((f) => (
            <div key={f.name} className={styles.glossaryFactor}>
              <div className={styles.glossaryFactorHeader}>
                <dt className={styles.glossaryFactorName}>{f.name}</dt>
                <span className={styles.glossaryFactorWeight}>{f.weight}</span>
              </div>
              <dd className={styles.glossaryFactorDesc}>{f.desc}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  </SidePanel>
);
